import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api, saveSession, SERVICES } from "@/lib/api";
import { collectPayment } from "@/lib/razorpay";
import Navbar from "@/components/karmivo/Navbar";
import HindiWarning from "@/components/karmivo/HindiWarning";
import { Building2, Check, CreditCard, Loader2, Copy } from "lucide-react";

export default function FranchiseApply() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [quote, setQuote] = useState(null);
  const [f, setF] = useState({
    applicant_name: "", business_name: "", phone: "", email: "", password: "",
    address: "", city: "Jaipur", state: "Rajasthan", pin_code: "",
    proposed_location: "", service_categories: ["daily_labour"], experience_years: 2,
    expected_capacity: 10, gstin: "", pan: "",
  });
  const [franchise, setFranchise] = useState(null);
  const [loading, setLoading] = useState(false);
  const upd = (k, v) => setF(x => ({ ...x, [k]: v }));
  const toggleCat = (c) => setF(x => ({ ...x, service_categories: x.service_categories.includes(c) ? x.service_categories.filter(y=>y!==c) : [...x.service_categories, c] }));

  useEffect(() => { api.get("/franchise/fee/quote").then(r => setQuote(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { ...f, email: f.email?.trim() || undefined };
      const { data } = await api.post(`/franchise/apply`, payload);
      saveSession("franchise_admin", data);
      setFranchise(data.franchise);
      toast.success("Application submitted. Pay the onboarding fee to continue.");
      setStep(1);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x => x.msg).join(", ") : (d || "Submission failed"));
    } finally { setLoading(false); }
  };

  const pay = async () => {
    setLoading(true);
    try {
      const { data: order } = await api.post(`/franchise/fee/create-order`, null, { params: { franchise_id: franchise.id } });
      const paid = await collectPayment(order, { description: "Franchise onboarding fee" });
      await api.post(`/franchise/fee/verify`, { franchise_id: franchise.id, ...paid });
      toast.success("Fee paid! Your application is now with Karmivo Admin.");
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || "Payment failed");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50"><Navbar/>
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-11 h-11 bg-orange-500 text-white rounded-xl flex items-center justify-center"><Building2 size={22}/></div>
          <div>
            <h1 className="font-display text-2xl font-bold text-slate-900">Become a Karmivo Franchise Partner</h1>
            <p className="text-sm text-slate-500">Build and operate a managed labour workforce under Karmivo's brand & tech.</p>
          </div>
        </div>

        {step === 0 && (
          <Card className="p-6" data-testid="franchise-apply-step">
            <HindiWarning compact/>
            <div className="grid sm:grid-cols-2 gap-4 mt-4">
              <div><Label>Applicant Name</Label><Input value={f.applicant_name} onChange={e => upd("applicant_name", e.target.value)} data-testid="fa-name"/></div>
              <div><Label>Business Name</Label><Input value={f.business_name} onChange={e => upd("business_name", e.target.value)} data-testid="fa-business"/></div>
              <div><Label>Phone</Label><Input value={f.phone} onChange={e => upd("phone", e.target.value)} data-testid="fa-phone"/></div>
              <div><Label>Email (optional)</Label><Input value={f.email} onChange={e => upd("email", e.target.value)} data-testid="fa-email"/></div>
              <div><Label>Password</Label><Input type="password" value={f.password} onChange={e => upd("password", e.target.value)} data-testid="fa-password"/></div>
              <div><Label>City</Label><Input value={f.city} onChange={e => upd("city", e.target.value)}/></div>
              <div><Label>State</Label><Input value={f.state} onChange={e => upd("state", e.target.value)}/></div>
              <div><Label>PIN Code</Label><Input value={f.pin_code} onChange={e => upd("pin_code", e.target.value)} data-testid="fa-pin"/></div>
              <div className="sm:col-span-2"><Label>Address</Label><Textarea value={f.address} onChange={e => upd("address", e.target.value)}/></div>
              <div className="sm:col-span-2"><Label>Proposed Franchise Location</Label><Input value={f.proposed_location} onChange={e => upd("proposed_location", e.target.value)}/></div>
              <div><Label>GSTIN (optional)</Label><Input value={f.gstin} onChange={e => upd("gstin", e.target.value)}/></div>
              <div><Label>PAN (optional)</Label><Input value={f.pan} onChange={e => upd("pan", e.target.value)}/></div>
              <div><Label>Experience (years)</Label><Input type="number" value={f.experience_years} onChange={e => upd("experience_years", parseInt(e.target.value||0))}/></div>
              <div><Label>Expected Workforce Capacity</Label><Input type="number" value={f.expected_capacity} onChange={e => upd("expected_capacity", parseInt(e.target.value||0))}/></div>
            </div>
            <div className="mt-5">
              <Label>Preferred Service Categories</Label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                {SERVICES.map(s => (
                  <button key={s.key} onClick={() => toggleCat(s.key)}
                    className={`py-2 px-3 rounded-lg border-2 text-xs font-semibold ${f.service_categories.includes(s.key) ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-600"}`}
                    data-testid={`fa-cat-${s.key}`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
            {quote && (
              <Card className="p-4 mt-5 bg-orange-50 border-orange-200">
                <div className="text-xs uppercase tracking-widest text-orange-700 font-bold">Onboarding Fee Preview</div>
                <div className="flex justify-between text-sm mt-2"><span>Base Fee</span><span className="font-mono font-bold">₹{quote.base.toLocaleString()}</span></div>
                <div className="flex justify-between text-sm"><span>GST @ {quote.gst_pct}%</span><span className="font-mono">₹{quote.gst.toLocaleString()}</span></div>
                <div className="flex justify-between text-base font-bold mt-1 pt-1 border-t border-orange-200"><span>Total Payable</span><span className="font-mono text-orange-700" data-testid="fee-quote-total">₹{quote.total.toLocaleString()}</span></div>
              </Card>
            )}
            <div className="mt-5 flex justify-end">
              <Button className="bg-orange-500 hover:bg-orange-600" onClick={submit} disabled={loading || !f.applicant_name || !f.phone || !f.password || !f.pin_code} data-testid="fa-submit-btn">
                {loading ? <Loader2 className="animate-spin mr-2" size={16}/> : null}Submit & Continue to Payment
              </Button>
            </div>
          </Card>
        )}

        {step === 1 && franchise && quote && (
          <Card className="p-6" data-testid="franchise-pay-step">
            <h2 className="font-display text-2xl font-bold text-slate-900">Pay Onboarding Fee</h2>
            <HindiWarning compact/>
            <Card className="p-4 mt-4 bg-slate-50 border-slate-200">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">Franchise</div>
              <div className="font-mono font-bold text-slate-900">{franchise.code}</div>
              <div className="mt-3 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-slate-600">Base Franchise Fee</span><span className="font-mono font-semibold">₹{quote.base.toLocaleString()}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">GST @ {quote.gst_pct}%</span><span className="font-mono">₹{quote.gst.toLocaleString()}</span></div>
                <div className="flex justify-between text-lg font-bold border-t border-slate-200 pt-2 mt-2"><span>Total Payable</span><span className="text-orange-600 font-mono">₹{quote.total.toLocaleString()}</span></div>
              </div>
              <p className="text-xs text-slate-500 mt-3">This is a one-time onboarding fee. It is NOT deducted from your future booking commissions. Payment is processed via Razorpay (currently in mock mode until keys are configured).</p>
            </Card>
            <div className="mt-5 flex justify-end">
              <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={pay} disabled={loading} data-testid="fa-pay-now-btn">
                {loading ? <Loader2 className="animate-spin mr-2" size={16}/> : <CreditCard size={16} className="mr-2"/>}Pay ₹{quote.total.toLocaleString()}
              </Button>
            </div>
          </Card>
        )}

        {step === 2 && franchise && (
          <Card className="p-6 text-center" data-testid="franchise-done-step">
            <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 flex items-center justify-center"><Check className="text-emerald-600" size={32}/></div>
            <h2 className="mt-4 font-display text-2xl font-bold text-slate-900">Payment Successful</h2>
            <p className="text-slate-600 mt-1">Your franchise application <span className="font-mono font-bold">{franchise.code}</span> is now with Karmivo Admin for review.</p>
            <div className="mt-5 flex gap-2 justify-center">
              <Button variant="outline" onClick={() => window.open(`${api.defaults.baseURL}/franchise/fee/invoice/${franchise.id}`, "_blank")} data-testid="fa-invoice-btn">Download Invoice</Button>
              <Button className="bg-orange-500 hover:bg-orange-600" onClick={() => nav("/franchise/dashboard")} data-testid="fa-goto-dashboard">Go to Dashboard</Button>
            </div>
          </Card>
        )}

        <div className="mt-6 text-center text-xs text-slate-500">Already a franchise? <Link to="/franchise/login" className="text-orange-600 font-semibold" data-testid="fa-login-link">Log in</Link></div>
      </div>
    </div>
  );
}
